import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  TrendingUp,
  ShoppingBag,
  Receipt,
  Trophy,
  CalendarDays,
  Clock,
  RefreshCw,
  ChevronRight,
  Download,
  Filter,
} from "lucide-react";
import api from "../api/api";
import { formatDateTime } from "../utils/datetime";

function RekapKasir() {
  const { sessionId } = useParams();
  const navigate      = useNavigate();

  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [payFilter, setPayFilter]   = useState("all");
  const [exporting, setExporting]   = useState(false);

  const fetchRecap = useCallback(async () => {
    try {
      setLoading(true);
      const url = payFilter === 'all'
        ? `/rekap/sessions/${sessionId}`
        : `/rekap/sessions/${sessionId}/filter?paymentMethod=${payFilter}`;
      const res = await api.get(url);
      setData(res.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Gagal mengambil rekap");
    } finally {
      setLoading(false);
    }
  }, [sessionId, payFilter]);

  useEffect(() => { fetchRecap(); }, [fetchRecap]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const url = `/rekap/sessions/${sessionId}/export${payFilter !== 'all' ? `?paymentMethod=${payFilter}` : ''}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `rekap-sesi-${sessionId}${payFilter !== 'all' ? `-${payFilter}` : ''}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error("Export error:", e);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-zinc-500">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Memuat rekap kasir...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const { session, summary, top_products, transactions } = data;

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      {/* Header */}
      <header className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/events/${session.event_id}`)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900"
            >
              <ArrowLeft className="h-4 w-4 text-zinc-600" />
            </button>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600 shadow-sm">
                <Receipt className="h-3 w-3 text-sky-600" />
                Rekap Kasir
              </div>
              <h1 className="mt-2 text-xl font-semibold tracking-tight text-zinc-900 md:text-2xl">
                {session.event_name}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] font-medium text-zinc-500">
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  Buka: {formatDateTime(session.opened_at)}
                </span>
                {session.closed_at && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Tutup: {formatDateTime(session.closed_at)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={fetchRecap} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={handleExport} disabled={exporting} className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-4 py-2 text-[13px] font-bold text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900">
              <Download className="h-4 w-4" />
              {exporting ? "Mengunduh..." : "Export Excel"}
            </button>
            <span className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider ring-1 ring-inset ${
                session.status === "closed" ? "bg-zinc-50 text-zinc-700 ring-zinc-500/20" : "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
              }`}>
              {session.status === "closed" ? "Sesi Ditutup" : "Sesi Terbuka"}
            </span>
          </div>
        </div>
      </header>

      {/* Filter bar */}
      <div className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white px-4 py-3 shadow-sm">
        <Filter className="h-4 w-4 text-zinc-400 shrink-0" />
        <span className="text-[13px] font-bold text-zinc-700 shrink-0">Filter Metode Pembayaran:</span>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setPayFilter("all")}
            className={`rounded-md px-3 py-1.5 text-[11px] font-bold border transition-all uppercase tracking-wider ${payFilter === 'all' ? 'bg-zinc-900 text-white border-zinc-900 shadow-sm' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
          >
            Semua
          </button>
          {['cash', 'qris'].map(method => (
            <button
              key={method}
              onClick={() => setPayFilter(method)}
              className={`rounded-md px-3 py-1.5 text-[11px] font-bold border transition-all uppercase tracking-wider ${payFilter === method ? 'bg-zinc-900 text-white border-zinc-900 shadow-sm' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
            >
              {method === 'cash' ? 'Cash / Tunai' : 'QRIS / Transfer'}
            </button>
          ))}
        </div>
        {payFilter !== 'all' && (
          <span className="ml-auto text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-1 rounded-md ring-1 ring-inset ring-indigo-600/20 uppercase tracking-widest">
            {payFilter} SAJA
          </span>
        )}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KpiCard
          title="Total Penjualan"
          value={fmtRp(summary.total_revenue)}
          icon={TrendingUp}
        />
        <KpiCard
          title="Total Transaksi"
          value={summary.transaction_count}
          icon={ShoppingBag}
        />
        <KpiCard
          title="Total Diterima (In)"
          value={fmtRp(summary.total_received)}
          icon={Receipt}
        />
        <KpiCard
          title="Kembalian (Out)"
          value={fmtRp(summary.total_change)}
          icon={CalendarDays}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        {/* Transaction list */}
        <div className="flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
          <div className="border-b border-zinc-100 bg-zinc-50/50 px-5 py-4 shrink-0">
            <h2 className="text-[15px] font-bold text-zinc-900">Rincian Transaksi</h2>
            <p className="mt-0.5 text-[12px] font-medium text-zinc-500">Seluruh struk transaksi dalam sesi kasir ini</p>
          </div>

          {transactions.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-50 border border-zinc-200 mb-3">
                <Receipt className="h-6 w-6 text-zinc-400" />
              </div>
              <h3 className="text-[14px] font-semibold text-zinc-900">Belum ada transaksi</h3>
              <p className="mt-1 text-[13px] text-zinc-500">Sesi ini belum memiliki transaksi yang tercatat.</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto bg-zinc-50/30 p-5 min-h-[400px] max-h-[800px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {transactions.map((tx) => (
                  <div key={tx.id} className="relative rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden flex flex-col group hover:border-zinc-300 transition-colors">
                    <div className="p-4 space-y-3">
                      {/* Header */}
                      <div className="flex justify-between items-start border-b border-dashed border-zinc-200 pb-3">
                        <div>
                          <div className="font-mono text-[13px] font-bold text-zinc-900">#TX-{tx.id.toString().padStart(4, '0')}</div>
                          <div className="text-[10px] font-medium text-zinc-400 mt-0.5">{formatDateTime(tx.created_at)}</div>
                        </div>
                        {tx.status === 'void' ? (
                          <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded uppercase tracking-wider ring-1 ring-inset ring-rose-600/20">Batal / Void</span>
                        ) : (
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded uppercase tracking-wider ring-1 ring-inset ring-emerald-600/20">{tx.payment_method}</span>
                        )}
                      </div>

                      {/* Items */}
                      <div className="space-y-1.5 border-b border-dashed border-zinc-200 pb-3 min-h-[40px]">
                        {(tx.items || []).map((item, idx) => (
                          <div key={idx} className="flex justify-between text-[11px] font-medium text-zinc-700">
                            <div className="flex-1 truncate pr-2 text-zinc-900">{item.product_name || `Item #${item.id}`}</div>
                            <div className="shrink-0 font-bold text-zinc-500 mr-2">{item.qty}x</div>
                            <div className="w-16 shrink-0 text-right font-bold tabular-nums text-zinc-900">{fmtRp(item.subtotal)}</div>
                          </div>
                        ))}
                        {(!tx.items || tx.items.length === 0) && (
                          <div className="text-[10px] text-zinc-400 italic">Rincian item tidak tersedia</div>
                        )}
                      </div>

                      {/* Totals */}
                      <div className="space-y-1.5 text-[11px] font-medium">
                        <div className="flex justify-between text-zinc-500">
                          <span>Total Pembelanjaan</span>
                          <span className="font-bold text-zinc-900 tabular-nums">{fmtRp(tx.total)}</span>
                        </div>
                        <div className="flex justify-between text-zinc-500">
                          <span className="uppercase tracking-wide">{tx.payment_method === 'cash' ? 'Tunai' : 'QRIS/TF'}</span>
                          <span className="tabular-nums">{fmtRp(tx.amount_paid)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-zinc-900 mt-2 pt-2 border-t border-zinc-100">
                          <span>Uang Kembali</span>
                          <span className="tabular-nums">{fmtRp(tx.change_given)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-between rounded-lg bg-zinc-900 px-6 py-5 text-white shadow-md">
                <span className="text-[14px] font-semibold text-zinc-300">Total Pendapatan Sesi Ini</span>
                <span className="text-xl font-bold tabular-nums tracking-tight">
                  {fmtRp(summary.total_revenue)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Top products */}
        <div className="space-y-6">
          <div className="flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-zinc-100 bg-zinc-50/50 px-5 py-4">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-zinc-900" />
                <h2 className="text-[15px] font-bold text-zinc-900">Produk Terlaris</h2>
              </div>
              <p className="mt-0.5 text-[12px] font-medium text-zinc-500">Diurutkan berdasarkan qty terjual sesi ini</p>
            </div>

            {top_products.length === 0 ? (
              <div className="px-5 py-12 text-center text-[13px] font-medium text-zinc-400">
                Belum ada data penjualan produk
              </div>
            ) : (
              <div className="divide-y divide-zinc-100 p-2">
                {top_products.map((p, i) => {
                  const max = top_products[0]?.total_qty || 1;
                  const pct = ((Number(p.total_qty) || 0) / max) * 100;
                  return (
                    <div key={p.code} className="px-4 py-3 rounded-md hover:bg-zinc-50 transition-colors">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-white text-[11px] font-bold text-zinc-700 shadow-sm">
                            {i + 1}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-[13px] font-bold text-zinc-900 leading-tight">
                              {p.product_name}
                            </div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-0.5">{p.code}</div>
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-[13px] font-bold tabular-nums text-zinc-900">
                            {p.total_qty} pcs
                          </div>
                          <div className="text-[11px] font-medium text-emerald-600 tabular-nums">
                            {fmtRp(p.total_revenue)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2.5 h-1.5 w-full rounded-full bg-zinc-100 overflow-hidden">
                        <div
                          className="h-1.5 rounded-full bg-zinc-900"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Link to event recap */}
          <Link
            to={`/rekap/event/${session.event_id}`}
            className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white px-5 py-4 shadow-sm hover:border-zinc-300 hover:shadow transition-all group"
          >
            <div>
              <div className="text-[14px] font-bold text-zinc-900 group-hover:text-indigo-600 transition-colors">Lihat Rekap Global Event</div>
              <div className="text-[12px] font-medium text-zinc-500 mt-0.5">Gabungan seluruh sesi kasir di event ini</div>
            </div>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-50 group-hover:bg-indigo-50 transition-colors">
              <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-indigo-600" />
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Card ────────────────────────────────────────────────

function KpiCard({ title, value, icon: Icon }) {
  return (
    <div className="flex flex-col justify-center rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-50 border border-zinc-100">
          <Icon className="h-4 w-4 text-zinc-600" />
        </div>
        <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">{title}</div>
      </div>
      <div className="text-2xl font-bold tracking-tight text-zinc-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────

function fmtRp(n) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);
}


export default RekapKasir;